import moment from 'moment'
import React from 'react'

import type DateRange from '../TimeframeFilter/DateRange'
import Switch from '../Switch/Switch'
import {
  DatePicker,
  FormLabel,
  hasValue,
  ListLoading,
  MultiSelect,
  type SearchBarProps,
  Select,
  type SelectProps,
  type TooltipProps,
} from '../../module'
import styles from './_filter.module.scss'
import { c } from '../../translations/LibraryTranslationService'

const TextInput = React.lazy(
  () => import(/* webpackChunkName: "textInput" */ '../Form/TextInput'),
)
const Picker = React.lazy(
  () => import(/* webpackChunkName: "picker" */ '../Picker/Picker'),
)

type FilterMenuPropsType<OptionType> = {
  /** An object containing an entry for each filter input component */
  states: FilterStatesType<OptionType>
  /** A callback for when the form closes */
  close: () => void
  /** A callback for when the date is reset on a date input */
  resetDate: React.ComponentProps<typeof DateRange>['resetDate']
  /** A callback for when the form's inputs change */
  onChangeCallout?: (...params: unknown[]) => void
  /** A boolean specifying whether the filters are still loading or not */
  loading?: boolean
  /** A boolean specifying whether to show additional child components above or below those specified in states */
  topChildren?: boolean
  /** A boolean specifying whether the sidedrawer with the filter form is open */
  isOpen: boolean
  /** Anything which you would like to add in addition to Filter options- unique filter types, header/footer information, etc  */
  children?: (props: {
    isOpen?: boolean
    close?: () => void
  }) => React.ReactNode
}

type FormLabelProps = React.ComponentProps<typeof FormLabel>

type FilterStateOptionType = {
  type: 'select' | 'dates' | 'text' | 'toggle' | 'switch' | 'multi-select'
  stateName: string
  rightLabel?: string
  customClass?: string
}

type DateRangeProps = React.ComponentProps<typeof DateRange>
type DatePickerProps = React.ComponentProps<typeof DatePicker>

interface DatesStatePropType extends FilterStateOptionType {
  type: 'dates'
  labelText: string
  disabled?: FormLabelProps['disabled']
  placement?: DateRangeProps['placement']
  defaultValue: {
    start_date: DateRangeProps['startDate']
    end_date?: DateRangeProps['endDate']
  }
  specifiedDay?: DateRangeProps['specifiedDay']
  hideCustomDateSearch?: DateRangeProps['hideCustomDateSearch']
  isOutsideRangeHandler?: DateRangeProps['isOutsideRangeHandler']
  dateRestrictions?: DatePickerProps['dateRestrictions']
  clearCallout?: DateRangeProps['resetDate']
  singleDate?: DateRangeProps['singleDate']
  showAllDates?: DateRangeProps['showAllDates']
}

type TextInputProps = React.ComponentProps<typeof TextInput>

interface TextStatePropType extends FilterStateOptionType {
  type: 'text'
  labelText: string
  defaultValue?: TextInputProps['value']
  onBlurCallout?: TextInputProps['onBlurCallout']
  inputType?: TextInputProps['type']
  clearCallout?: TextInputProps['clearCallout']
  onReturnCallout?: TextInputProps['onReturnCallout']
  numberProps?: Pick<
    TextInputProps,
    'min' | 'max' | 'step' | 'onlyWholeNumbers' | 'onlyPositiveNumbers'
  > & { suffix?: string }
  placeholder?: TextInputProps['placeholder']
  disabled?: TextInputProps['disabled']
  numberFormat?: TextInputProps['numberFormat']
  inputLabel?: TextInputProps['inputLabel']
  labelTooltip?: TextInputProps['labelTooltip']
  keyUp?: () => void
  shouldClose?: boolean
}

type PickerProps = React.ComponentProps<typeof Picker>

interface ToggleStatePropType extends FilterStateOptionType {
  type: 'toggle'
  labelText?: string
  options: PickerProps['options']
  state?: PickerProps['state']
  defaultValue?: PickerProps['selected']
  labelTooltip?: PickerProps['labelTooltip']
  disabled?: boolean
}

type MultiSelectProps = React.ComponentProps<typeof MultiSelect>

type MultiSelectStatePropType = FilterStateOptionType &
  Omit<MultiSelectProps, 'callout'> & {
    type: 'multi-select'
  }

type SelectStatePropType<OptionType> = FilterStateOptionType & {
  type: 'select'
  options: SelectProps<OptionType>['options']
  labelText: string
  loading?: SelectProps<OptionType>['loading']
  labelTooltip?: TooltipProps
  defaultValue: SelectProps<OptionType>['selectedItem']
  optionKeyName: string
  isError?: SelectProps<OptionType>['error']
  searchBar?: boolean
  required?: boolean
  disabled?: boolean
  onSearchChange?: SearchBarProps['onChange']
}

type SwitchProps = React.ComponentProps<typeof Switch>

interface SwitchStatePropType extends FilterStateOptionType {
  type: 'switch'
  labelText: string
  labelTooltip: FormLabelProps['tooltip']
  disabled?: FormLabelProps['disabled']
  defaultValue: SwitchProps['checked']
}

export type FilterStatesType<OptionType> = Record<
  string,
  | SelectStatePropType<OptionType>
  | DatesStatePropType
  | TextStatePropType
  | ToggleStatePropType
  | SwitchStatePropType
  | MultiSelectStatePropType
>

export const FilterMenu = <OptionType,>({
  states,
  close,
  onChangeCallout,
  loading,
  topChildren,
  isOpen,
  children,
}: FilterMenuPropsType<OptionType>): React.JSX.Element => {
  const updateFilters = (name: string, value: unknown) => {
    onChangeCallout?.(name, value)
  }
  const handleInput = (name: string, value: unknown, textInput?: string) => {
    if (!textInput) {
      hasValue(value) && updateFilters(name, value)
    } else {
      updateFilters(name, value)
    }
  }

  const clearInput = (name: string, clearCallout: (() => void) | undefined) => {
    handleInput(name, '', 'textInput')
    clearCallout?.()
  }

  const handleDates = ({
    start,
    end,
    stateName,
  }: {
    start: DatesStatePropType['defaultValue']['start_date']
    end?: DatesStatePropType['defaultValue']['end_date']
    stateName: string
  }) => {
    onChangeCallout?.(stateName ?? 'dates', {
      start_date: start,
      ...(end ? { end_date: end } : {}),
    })
  }

  const inputKeyUp = (callout?: () => void, shouldClose?: boolean) => {
    if (callout) {
      callout()
    }
    if (shouldClose) {
      close()
    }
  }

  return (
    <>
      {!loading ? (
        <div className={styles.filterMenuStyle}>
          {topChildren && children?.({ close, isOpen })}
          <React.Suspense fallback={<ListLoading />}>
            {Object.values(states).map((s) => {
              switch (s.type) {
                case 'select':
                  return (
                    <Select
                      key={s?.stateName || s?.labelText}
                      labelProps={{
                        label: s.labelText,
                        tooltip: s?.labelTooltip,
                        rightLabel: s?.rightLabel,
                      }}
                      options={s.options}
                      selectedItem={s.defaultValue}
                      optionKeyName={
                        s.optionKeyName as SelectProps<OptionType>['optionKeyName']
                      }
                      labelKeyName={
                        s.optionKeyName as SelectProps<OptionType>['optionKeyName']
                      }
                      onChange={(option) => {
                        handleInput(s.stateName, option)
                      }}
                      required={s.required}
                      error={s.isError}
                      disabled={
                        s.disabled || !(s.options.length > 0) || s.isError
                      }
                      searchBarProps={{
                        showSearchBar: s.searchBar,
                        onChange: s.onSearchChange,
                      }}
                    />
                  )
                case 'dates':
                  return (
                    <div key={s?.stateName || s?.labelText}>
                      <FormLabel label={s.labelText} disabled={s?.disabled} />
                      {s.singleDate ? (
                        <DatePicker
                          type='single'
                          selectedDate={
                            (states[s.stateName] as DatesStatePropType)
                              ?.defaultValue.start_date
                              ? moment(
                                  (states[s.stateName] as DatesStatePropType)
                                    ?.defaultValue.start_date,
                                ).toDate()
                              : undefined
                          }
                          onDateChange={(date) => {
                            if (date) {
                              handleDates({
                                start: date,
                                ...(!s.singleDate ? { end: undefined } : {}),
                                stateName: s.stateName,
                              })
                            } else {
                              handleDates({
                                start: undefined,
                                ...(!s.singleDate ? { end: undefined } : {}),
                                stateName: s.stateName,
                              })
                            }
                          }}
                          dateRestrictions={s.dateRestrictions}
                        />
                      ) : (
                        <DatePicker
                          type='range'
                          selectedDateRange={{
                            from: (states[s.stateName] as DatesStatePropType)
                              ?.defaultValue.start_date
                              ? moment(
                                  (states[s.stateName] as DatesStatePropType)
                                    ?.defaultValue.start_date,
                                ).toDate()
                              : undefined,
                            to: (states[s.stateName] as DatesStatePropType)
                              ?.defaultValue.end_date
                              ? moment(
                                  (states[s.stateName] as DatesStatePropType)
                                    ?.defaultValue.end_date,
                                ).toDate()
                              : undefined,
                          }}
                          onDateChange={(date) => {
                            if (date && 'from' in date) {
                              const { from, to } = date
                              handleDates({
                                start: from,
                                ...(!s.singleDate ? { end: to } : {}),
                                stateName: s.stateName,
                              })
                            } else {
                              handleDates({
                                start: undefined,
                                ...(!s.singleDate ? { end: undefined } : {}),
                                stateName: s.stateName,
                              })
                            }
                          }}
                          dateRestrictions={s.dateRestrictions}
                        />
                      )}
                    </div>
                  )
                case 'text':
                  return (
                    <div key={s?.stateName || s?.labelText}>
                      {s?.inputType === 'number' ? (
                        <TextInput
                          value={s.defaultValue || ''}
                          labelTooltip={s?.labelTooltip}
                          rightLabel={s?.rightLabel}
                          onBlurCallout={
                            s.onBlurCallout ? s.onBlurCallout : undefined
                          }
                          callout={(name, value) =>
                            handleInput(name as string, value, 'textInput')
                          }
                          onReturnCallout={() => {
                            s.onReturnCallout?.()
                            s.shouldClose && close()
                          }}
                          type={'number'}
                          stateName={s.stateName}
                          classType={`${s.customClass ? s.customClass : ''}`}
                          labelText={s.labelText}
                          keyUp={() => inputKeyUp(s.keyUp, s.shouldClose)}
                          clear={Boolean(s.clearCallout)}
                          clearCallout={() =>
                            clearInput(s.stateName, s.clearCallout)
                          }
                          placeholder={
                            s?.numberProps?.suffix
                              ? s.placeholder + s?.numberProps?.suffix
                              : s.placeholder
                          }
                          {...(s?.numberProps
                            ? {
                                min: s?.numberProps?.min,
                                max: s?.numberProps?.max,
                                step: s?.numberProps?.step,
                                maxLength: s?.numberProps?.max,
                                onlyWholeNumbers:
                                  s?.numberProps?.onlyWholeNumbers,
                                onlyPositiveNumbers:
                                  s?.numberProps?.onlyPositiveNumbers,
                              }
                            : {})}
                          numberFormat={s?.numberFormat}
                          inputLabel={s?.inputLabel}
                        />
                      ) : (
                        <TextInput
                          value={s.defaultValue || ''}
                          labelTooltip={s?.labelTooltip}
                          rightLabel={s?.rightLabel}
                          onBlurCallout={
                            s.onBlurCallout ? s.onBlurCallout : undefined
                          }
                          callout={(name, value) =>
                            handleInput(name as string, value, 'textInput')
                          }
                          onReturnCallout={() => {
                            s.onReturnCallout?.()
                            s.shouldClose && close()
                          }}
                          type={s?.inputType ? s?.inputType : 'text'}
                          stateName={s.stateName}
                          classType={`${s.customClass ? s.customClass : ''}`}
                          labelText={s.labelText}
                          keyUp={() => inputKeyUp(s.keyUp, s.shouldClose)}
                          clear={Boolean(s.clearCallout)}
                          clearCallout={() =>
                            clearInput(s.stateName, s.clearCallout)
                          }
                          placeholder={
                            s?.numberProps?.suffix
                              ? s.placeholder + s?.numberProps?.suffix
                              : s.placeholder
                          }
                          inputLabel={s?.inputLabel}
                        />
                      )}
                    </div>
                  )
                case 'toggle':
                  return (
                    <div key={s?.stateName || s?.labelText}>
                      <Picker
                        options={s.options}
                        labelTooltip={s?.labelTooltip}
                        callout={(val) => handleInput(s?.stateName, val)}
                        stateName={s.stateName}
                        selected={s.defaultValue}
                        customClass={s.customClass}
                        rightLabel={s.rightLabel}
                        labelText={s.labelText}
                        disabled={s?.disabled}
                      />
                    </div>
                  )
                case 'multi-select':
                  return (
                    <MultiSelect
                      formLabelProps={s?.formLabelProps}
                      options={s?.options}
                      labelKey={s?.labelKey}
                      loading={s?.loading ?? false}
                      selectedOptions={s?.selectedOptions}
                      emptyStateProps={s?.emptyStateProps}
                      selectPlaceholder={
                        s.options.length > 0
                          ? s.selectPlaceholder || ''
                          : c('noOptionsAvailable')
                      }
                      searchBarProps={s?.searchBarProps}
                      nestedConfig={s?.nestedConfig}
                      dropdownState={s?.dropdownState}
                      callout={(selectedList) =>
                        updateFilters(
                          s?.stateName,
                          selectedList.map((item) => item[s?.labelKey]),
                        )
                      }
                      fetchData={s?.fetchData as () => void}
                      isFetching={s?.isFetching}
                      isReorderable={s?.isReorderable}
                      disabled={s?.disabled}
                      hideSelectAll={s?.hideSelectAll}
                      sortAlphabetically={s?.sortAlphabetically}
                      hasMore={s?.hasMore ?? false}
                    />
                  )
                case 'switch':
                  return (
                    <div key={s?.stateName || s?.labelText}>
                      <FormLabel
                        label={s.labelText}
                        tooltip={s?.labelTooltip}
                        rightLabel={s?.rightLabel}
                        disabled={s?.disabled}
                      />
                      <Switch
                        checked={s.defaultValue}
                        callout={(value) => {
                          handleInput(s.stateName, value)
                        }}
                      />
                    </div>
                  )
                default:
                  return null
              }
            })}
          </React.Suspense>
          {!topChildren && children?.({ close, isOpen })}
        </div>
      ) : (
        <ListLoading />
      )}
    </>
  )
}
